"use client"
import React from 'react'
import { useSession } from 'next-auth/react'

interface RBACGuardProps {
  children: React.ReactNode
  allowedRoles: string[]
  fallback?: React.ReactNode
  requireAuth?: boolean
}

/**
 * Role-Based Access Control Guard Component
 * Conditionally renders children based on user role
 */
export function RBACGuard({
  children,
  allowedRoles,
  fallback = null,
  requireAuth = true
}: RBACGuardProps) {
  const { data: session, status } = useSession()

  // Show loading state while checking authentication
  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    )
  }

  // Check if authentication is required and user is not authenticated
  if (requireAuth && !session) {
    return fallback || null
  }

  // Check if user has required role
  if (session?.user?.role && allowedRoles.includes(session.user.role)) {
    return <>{children}</>
  }

  // User doesn't have required role
  return fallback || null
}

/**
 * Admin-only component wrapper
 */
export function AdminOnly({ children, fallback }: { children: React.ReactNode, fallback?: React.ReactNode }) {
  return (
    <RBACGuard allowedRoles={['Admin']} fallback={fallback}>
      {children}
    </RBACGuard>
  )
}

/**
 * Editor and Admin component wrapper
 */
export function EditorAndAbove({ children, fallback }: { children: React.ReactNode, fallback?: React.ReactNode }) {
  return (
    <RBACGuard allowedRoles={['Admin', 'Editor']} fallback={fallback}>
      {children}
    </RBACGuard>
  )
}

/**
 * Author and above component wrapper
 */
export function AuthorAndAbove({ children, fallback }: { children: React.ReactNode, fallback?: React.ReactNode }) {
  return (
    <RBACGuard allowedRoles={['Admin', 'Editor', 'Author']} fallback={fallback}>
      {children}
    </RBACGuard>
  )
}

/**
 * Hook to check user permissions
 */
export function useRBAC() {
  const { data: session } = useSession()

  const hasRole = (roles: string[]) => {
    return session?.user?.role ? roles.includes(session.user.role) : false
  }

  const isAdmin = () => hasRole(['Admin'])
  const isEditor = () => hasRole(['Admin', 'Editor'])
  const isAuthor = () => hasRole(['Admin', 'Editor', 'Author'])

  return {
    user: session?.user,
    role: session?.user?.role,
    hasRole,
    isAdmin,
    isEditor,
    isAuthor
  }
}
